import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, NgZone, Injector, runInInjectionContext, OnDestroy } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { fromEvent, merge, Observable, Subscription, timer, firstValueFrom } from 'rxjs';
import { startWith, switchMap, take } from 'rxjs/operators';
import { environment } from '../../environments/environment';

@Injectable({
	providedIn: 'root'
})
export class SessionsService implements OnDestroy {
	private readonly INACTIVITY_TIMEOUT_MINUTES = 20;
	private readonly INACTIVITY_TIMEOUT_MS = this.INACTIVITY_TIMEOUT_MINUTES * 60 * 1000;

	private lastActivityTimestamp: number;

	private userActivity$: Observable<any>;
	private timerSubscription: Subscription;
	private visibilitySubscription: Subscription;

	private monitoring = false;

	private channel: BroadcastChannel;

	constructor(
		private ngZone: NgZone,
		private http: HttpClient,
		private afs: AngularFirestore,
		private injector: Injector
	) {
		this.channel = new BroadcastChannel('SESSIONS_SYNC');

		this.channel.onmessage = (event) => {
			if (event.data) {
				this.lastActivityTimestamp = event.data;
				console.log("UPDATED", this.lastActivityTimestamp);
			}
		};
	}

	ngOnDestroy() {
		this.stopMonitoring();
	}

	public async startMonitoring() {
		if (!localStorage.getItem('khdnmasc')) {
			localStorage.setItem('khdnmasc', 'knxyyws');

			let user = JSON.parse(localStorage.getItem('currentUser'));
			let userMeta = JSON.parse(localStorage.getItem('currentUserMeta'));
			let userUid = user.uid;

			var currentOS = "";

			const userAgent = window.navigator.userAgent.toLowerCase();
			const platform = (window.navigator.platform || '').toLowerCase();

			if (/iphone|ipad|ipod/.test(userAgent)) {
				currentOS = "iOS";
			} else if (/android/.test(userAgent)) {
				currentOS = "Android";
			} else if (/windows nt/.test(userAgent) || platform.indexOf('win') !== -1) {
				currentOS = "Windows";
			} else if (/macintosh|macintel|macppc|mac68k/.test(userAgent) || platform.indexOf('mac') !== -1) {
				currentOS = "MacOS";
			} else if (/linux/.test(userAgent) || platform.indexOf('linux') !== -1) {
				currentOS = "Linux";
			} else {
				currentOS = "Unknown";
			}

			try {
				var serverTime = await firstValueFrom(this.getServerTime());
				const serverTimeTS = Math.round(serverTime.time / 1000);

				if (!userMeta.school) delete userMeta.school;
				if (!userMeta.class) delete userMeta.class;

				await runInInjectionContext(this.injector, () => this.afs.collection('userData').doc(userUid).collection('sessions').add({ date: serverTimeTS, userAgent: currentOS, studentId: userUid, context: 'self', ...userMeta }));
			} catch (err) {
				console.error(err);
			}
		}

		if (this.monitoring) return;

		this.monitoring = true;
		this.updateLastActivityTimestamp();

		this.userActivity$ = merge(
			fromEvent(window, 'mousemove'),
			fromEvent(window, 'mousedown'),
			fromEvent(window, 'keydown'),
			fromEvent(window, 'touchstart'),
			fromEvent(window, 'scroll')
		);

		this.ngZone.runOutsideAngular(() => {
			this.timerSubscription = this.userActivity$.pipe(
				startWith(0),
				switchMap(() => {
					this.updateLastActivityTimestamp();
					return timer(this.INACTIVITY_TIMEOUT_MS);
				}),
				take(1)
			).subscribe(() => {
				this.ngZone.run(() => this.expireSession());
			});

			this.visibilitySubscription = fromEvent(document, 'visibilitychange').subscribe(() => {
				if (document.visibilityState === 'visible') {
					this.checkInactivity();
				}
			});
		});
	}

	public stopMonitoring() {
		if (this.timerSubscription) {
			this.timerSubscription.unsubscribe();
		}
		if (this.visibilitySubscription) {
			this.visibilitySubscription.unsubscribe();
		}
	}

	private updateLastActivityTimestamp(): void {
		this.lastActivityTimestamp = Date.now();
		this.channel.postMessage(this.lastActivityTimestamp);
	}

	private checkInactivity(): void {
		const elapsed = Date.now() - this.lastActivityTimestamp;
		if (elapsed > this.INACTIVITY_TIMEOUT_MS) {
			this.ngZone.run(() => this.expireSession());
		}
	}

	private expireSession() {
		this.monitoring = false;

		localStorage.removeItem('khdnmasc');

		this.stopMonitoring();
	}

	private getServerTime(): Observable<any> {
		return this.http.get<any>(environment.firebaseFunctionUrl + 'getCurrentTime', {
			headers: new HttpHeaders({
				'Content-Type': 'application/json',
				'Accept': 'application/json'
			})
		});
	}
}


